import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, ExternalLink, Settings as SettingsIcon, AlertCircle, RefreshCw } from 'lucide-react';
import api from '@/lib/api';
import { useUser } from '@/lib/auth';

export default function AdminTrafficStats() {
  const user = useUser();
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  const load = () => {
    setLoading(true);
    api.get('/admin/traffic-embed')
      .then((r) => setUrl(r.data?.looker_studio_url || ''))
      .catch(() => setUrl(''))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const isSuper = !!user?.is_super_admin;

  return (
    <AdminLayout title="Statistiche Traffico">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2 text-sm text-[color:var(--text-muted)]">
          <TrendingUp className="w-4 h-4 text-[color:var(--gold)]"/>
          <span>Dashboard Google Looker Studio integrata</span>
          {url && <Badge variant="outline" className="border-emerald-500/40 text-emerald-300">Connesso</Badge>}
        </div>
        <div className="flex gap-2">
          {url && (
            <>
              <Button data-testid="traffic-reload" onClick={() => setReloadKey(k => k+1)} variant="outline" className="border-[color:var(--gold)] text-[color:var(--gold)]"><RefreshCw className="w-4 h-4 mr-2"/>Ricarica</Button>
              <a href={url} target="_blank" rel="noreferrer"><Button data-testid="traffic-open-external" variant="ghost"><ExternalLink className="w-4 h-4 mr-2"/>Apri in Looker</Button></a>
            </>
          )}
          {isSuper && (<Link to="/admin/settings"><Button variant="ghost" data-testid="traffic-go-settings"><SettingsIcon className="w-4 h-4 mr-2"/>Imposta URL</Button></Link>)}
        </div>
      </div>

      {loading ? (
        <div className="rounded-2xl border border-[color:var(--border-c)] bg-[color:var(--surface)] h-[80vh] flex items-center justify-center text-[color:var(--text-muted)]">Caricamento…</div>
      ) : !url ? (
        <div className="rounded-2xl border border-dashed border-[color:var(--border-c)] bg-[color:var(--surface)] p-12 text-center" data-testid="traffic-empty-state">
          <AlertCircle className="w-12 h-12 mx-auto text-[color:var(--gold)] opacity-50 mb-3"/>
          <h2 className="font-heading text-2xl mb-2">Dashboard Looker Studio non configurata</h2>
          <p className="text-sm text-[color:var(--text-muted)] max-w-xl mx-auto mb-6">
            {isSuper
              ? "Vai in Impostazioni e incolla l'URL di embed del tuo report Looker Studio per visualizzare qui i grafici di Analytics."
              : "Il Super Admin deve impostare l'URL del report Looker Studio. Contattalo per attivare la dashboard."}
          </p>
          {isSuper && (
            <div className="space-y-2 text-left max-w-xl mx-auto text-xs text-[color:var(--text-subtle)]">
              <p><b className="text-[color:var(--gold)]">Come ottenere l'URL:</b></p>
              <ol className="list-decimal pl-5 space-y-1">
                <li>Apri il tuo report su <a href="https://lookerstudio.google.com" target="_blank" rel="noreferrer" className="text-[color:var(--gold)] hover:underline">Looker Studio</a></li>
                <li>Clicca su <b>Condividi → Incorpora report</b></li>
                <li>Attiva l'incorporamento e copia l'URL nel campo <code>src</code> dell'iframe</li>
                <li>Incollalo in Impostazioni → <b>URL Looker Studio</b></li>
              </ol>
              <Link to="/admin/settings" className="inline-block mt-4"><Button data-testid="traffic-empty-cta" className="bg-[color:var(--gold-strong)] hover:bg-[#C6A21A] text-black"><SettingsIcon className="w-4 h-4 mr-2"/>Vai alle Impostazioni</Button></Link>
            </div>
          )}
        </div>
      ) : (
        <div className="rounded-2xl border border-[color:var(--border-c)] bg-[color:var(--surface)] overflow-hidden" data-testid="traffic-iframe-wrapper">
          <iframe
            key={reloadKey}
            data-testid="traffic-iframe"
            title="Looker Studio Dashboard"
            src={url}
            className="w-full"
            style={{ height: '85vh', border: 0 }}
            allowFullScreen
            sandbox="allow-storage-access-by-user-activation allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
          />
        </div>
      )}
    </AdminLayout>
  );
}
